import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo
} from "react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from "recharts";

import {
  FaUsers,
  FaTruck,
  FaBoxes,
  FaShoppingCart,
  FaArrowCircleLeft,
  FaUserCog,
  FaChartLine,
  FaThLarge,
  FaSyncAlt,
  FaArrowUp,
  FaShoppingBag,
  FaMoneyBillWave,
  FaFileInvoiceDollar
} from "react-icons/fa";

import { MdInventory } from "react-icons/md";

import {
  FiAlertCircle,
  FiTrendingUp,
  FiActivity,
  FiPackage
} from "react-icons/fi";

import { useAuth } from "../module_roots/AuthContext";
// import "../styles/dashboard-date-range.css";

/* ============================================================
   CONSTANTS
============================================================ */

const SALES_YELLOW = "#f5b91e";
const PURCHASE_BLUE = "#3b82f6";
const SUCCESS_GREEN = "#10b981";
const DANGER_RED = "#ef4444";
const DASHBOARD_ANALYTICS_URL =
  "https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_dashboard_analytics.php";

const DASHBOARD_TIMEZONE = "Asia/Karachi";
const rangeISO = date => date.toISOString().slice(0, 10);
function dashboardRange(preset, now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: DASHBOARD_TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const part = type => parts.find(p => p.type === type).value;
  const today = new Date(`${part("year")}-${part("month")}-${part("day")}T00:00:00Z`);
  let start = new Date(today), end = new Date(today);
  if (preset === "yesterday") { start.setUTCDate(start.getUTCDate() - 1); end = new Date(start); }
  if (preset === "last_week") {
    const daysSinceMonday = (today.getUTCDay() + 6) % 7;
    end.setUTCDate(end.getUTCDate() - daysSinceMonday - 1);
    start = new Date(end); start.setUTCDate(start.getUTCDate() - 6);
  }
  if (preset === "last_month") {
    end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0));
    start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  }
  return { preset, startDate: rangeISO(start), endDate: rangeISO(end) };
}
function validDashboardDates(start, end) {
  const parse = value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
    const d = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(d.getTime()) && rangeISO(d) === value ? d.getTime() : NaN;
  };
  const a = parse(start), b = parse(end);
  return Number.isFinite(a) && Number.isFinite(b) && a <= b && (b - a) / 86400000 < 366;
}
const DATE_LABELS = {
  en: { today: "Today", yesterday: "Yesterday", last_week: "Last week", last_month: "Last month", custom: "Custom dates", from: "From", to: "To", apply: "Apply", range: "Date range", periodSales: "Sales in selected period", invoices: "invoices in selected period", invalid: "Choose valid dates in order, up to 366 days.", current: "Inventory and customer, supplier and employee counts show current totals.", loading: "Updating analytics…" },
  ur: { today: "آج", yesterday: "کل", last_week: "گزشتہ ہفتہ", last_month: "گزشتہ مہینہ", custom: "اپنی تاریخیں", from: "سے", to: "تک", apply: "لاگو کریں", range: "تاریخ کی حد", periodSales: "منتخب مدت کی فروخت", invoices: "منتخب مدت کے بل", invalid: "درست ترتیب میں زیادہ سے زیادہ 366 دن منتخب کریں۔", current: "اسٹاک، گاہکوں، سپلائرز اور ملازمین کی تعداد موجودہ حالت دکھاتی ہے۔", loading: "اعداد و شمار تازہ ہو رہے ہیں…" }
};

const fetchDashboardAnalytics = async (shopId, filters, token, signal) => {
  const response = await fetch(DASHBOARD_ANALYTICS_URL, {
    method: "POST",
    signal,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({
      shop_id: shopId,
      preset: filters.preset,
      start_date: filters.startDate,
      end_date: filters.endDate
    })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status !== "success") {
    throw new Error(data.message || "Unable to load dashboard analytics");
  }
  return data;
};


/* ============================================================
   THEME COLOR
   Reads current app theme from CSS + localStorage.

   IMPORTANT:
   CSS variable --primary-color is still preferred when your
   global theme system has already applied the selected theme.
============================================================ */

const isValidCSSColor = (value) => {
  if (!value || typeof value !== "string") return false;

  if (typeof document === "undefined") return true;

  const test = document.createElement("span");
  test.style.color = "";
  test.style.color = value.trim();

  return test.style.color !== "";
};


const extractColorFromThemeValue = (rawValue) => {
  if (!rawValue) return null;

  const trimmed = String(rawValue).trim();

  /* Direct stored color */
  if (isValidCSSColor(trimmed)) {
    /*
      Ignore values that are theme mode names rather than colors.
    */
    const lower = trimmed.toLowerCase();

    if (
      lower !== "dark" &&
      lower !== "light" &&
      lower !== "system" &&
      lower !== "default"
    ) {
      return trimmed;
    }
  }

  /* JSON theme object */
  try {
    const parsed = JSON.parse(trimmed);

    if (parsed && typeof parsed === "object") {
      const possibleColors = [
        parsed.primaryColor,
        parsed.primary_color,
        parsed.primary,
        parsed.accentColor,
        parsed.accent_color,
        parsed.accent,
        parsed.themeColor,
        parsed.theme_color,
        parsed.color
      ];

      for (const color of possibleColors) {
        if (
          typeof color === "string" &&
          isValidCSSColor(color)
        ) {
          return color.trim();
        }
      }
    }
  } catch (error) {
    /* Not JSON — completely fine */
  }

  return null;
};


const getDashboardThemeColor = () => {
  if (typeof window === "undefined") {
    return "#10b981";
  }

  /*
   * 1. Check common localStorage theme/color keys.
   *
   * Add your exact key to this array if your application
   * uses a different name.
   */
  const storageKeys = [
    "ourzest_theme_color",
    "ourzest_primary_color",
    "ourzest_theme",
    "themeColor",
    "primaryColor",
    "primary_color",
    "accentColor",
    "accent_color",
    "theme"
  ];

  for (const key of storageKeys) {
    const stored = localStorage.getItem(key);
    const extracted = extractColorFromThemeValue(stored);

    if (extracted) {
      return extracted;
    }
  }

  /*
   * 2. Read the actual theme CSS variable used by the app.
   */
  const rootStyles = getComputedStyle(
    document.documentElement
  );

  const cssPrimary = rootStyles
    .getPropertyValue("--primary-color")
    .trim();

  if (cssPrimary && isValidCSSColor(cssPrimary)) {
    return cssPrimary;
  }

  /*
   * 3. Check body in case variables are scoped there.
   */
  const bodyStyles = getComputedStyle(document.body);

  const bodyPrimary = bodyStyles
    .getPropertyValue("--primary-color")
    .trim();

  if (
    bodyPrimary &&
    isValidCSSColor(bodyPrimary)
  ) {
    return bodyPrimary;
  }

  return "#10b981";
};


/* ============================================================
   PKR FORMATTERS
============================================================ */

const formatPKR = (value) => {
  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "₨0";
  }

  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
    .format(num)
    .replace("PKR", "₨");
};


const formatCompactPKR = (value) => {
  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "₨0";
  }

  if (Math.abs(num) >= 10000000) {
    return `₨${(num / 10000000).toFixed(1)}Cr`;
  }

  if (Math.abs(num) >= 100000) {
    return `₨${(num / 100000).toFixed(1)}L`;
  }

  if (Math.abs(num) >= 1000) {
    return `₨${(num / 1000).toFixed(1)}K`;
  }

  return `₨${Math.round(num)}`;
};


const formatNodeValue = (value) => {
  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "0";
  }

  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(1)}Cr`;
  }

  if (num >= 100000) {
    return `${(num / 100000).toFixed(1)}L`;
  }

  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }

  return Math.round(num).toLocaleString("en-PK");
};


/* ============================================================
   TRANSLATIONS
============================================================ */

const DASHBOARD_LANGUAGES = {
  en: { dir: "ltr", locale: "en-PK" },
  ur: { dir: "rtl", locale: "ur-PK" },
  ps: { dir: "rtl", locale: "ps-AF" },
  ar: { dir: "rtl", locale: "ar-SA" },
  tr: { dir: "ltr", locale: "tr-TR" },
  fa: { dir: "rtl", locale: "fa-IR" },
  prs: { dir: "rtl", locale: "fa-AF" }
};

const DASHBOARD_TRANSLATIONS = {
  en: {
    todaysSales: "Today's Sales",
    totalCustomers: "Total Customers",
    suppliers: "Suppliers",
    products: "Products",
    inventoryValue: "Inventory Value",
    lowStockAlert: "Low Stock Alert",
    totalReturns: "Total Returns",
    totalEmployees: "Total Employees",

    cardsView: "Cards",
    chartsView: "Charts",

    revenueTrend: "Sales & Revenue Trend",
    inventoryTrend: "Stock & Inventory Health",
    businessComparison: "Sales vs Purchases",

    overview: "Overview Analytics",
    overviewSub: "Live business intelligence & performance",

    noData: "No analytics available",
    loadingAnalytics: "Loading analytics",
    preparingDashboard: "Preparing your business dashboard...",
    refreshDashboard: "Refresh dashboard",
    salesMovement: "Sharp node-by-node sales movement",
    todaysRevenue: "Today's revenue",
    trending: "Trending",
    comparisonMovement: "Sharp comparison movement",
    sales: "Sales",
    purchases: "Purchases",
    paid: "Paid",
    dues: "Dues",
    purchaseRatio: "Purchase Ratio",
    inventoryOperationalHealth: "Inventory operational health",
    healthy: "Healthy",
    stock: "Stock",
    customers: "Customers",
    returns: "Returns",
    health: "Health",
    lowStock: "Low Stock",
    totalPurchases: "Total Purchases",
    currentMonth: "Current month",
    totalPaid: "Total Paid",
    nonReturnedSales: "Non-returned sales",
    totalDues: "Total Dues",
    outstandingSales: "Outstanding non-returned sales",
    registeredCustomers: "Registered customers",
    activeSupplierNetwork: "Active supplier network",
    currentStockValuation: "Current stock valuation",
    activeInventoryItems: "Active inventory items",
    requiresReorder: "Requires reorder",
    urgent: "Urgent",
    invoicesToday: "invoices today",
    amount: "Amount",
    activeEmployees: "active employees"
  },
  ur: {
    todaysSales: "آج کی فروخت", totalCustomers: "کل گاہک", suppliers: "سپلائرز", products: "مصنوعات",
    inventoryValue: "انوینٹری کی مالیت", lowStockAlert: "کم اسٹاک کا انتباہ", totalReturns: "کل واپسی", totalEmployees: "کل ملازمین",
    cardsView: "کارڈز", chartsView: "چارٹس", revenueTrend: "فروخت اور آمدنی کا رجحان", inventoryTrend: "اسٹاک اور انوینٹری کی حالت",
    businessComparison: "فروخت بمقابلہ خریداری", overview: "مجموعی تجزیات", overviewSub: "براہ راست کاروباری معلومات اور کارکردگی",
    noData: "کوئی تجزیاتی ڈیٹا دستیاب نہیں", loadingAnalytics: "تجزیات لوڈ ہو رہے ہیں", preparingDashboard: "آپ کا کاروباری ڈیش بورڈ تیار ہو رہا ہے...",
    refreshDashboard: "ڈیش بورڈ تازہ کریں", salesMovement: "فروخت کی مرحلہ وار حرکت", todaysRevenue: "آج کی آمدنی", trending: "رجحان میں",
    comparisonMovement: "تقابلی حرکت", sales: "فروخت", purchases: "خریداری", paid: "ادا شدہ", dues: "واجب الادا", purchaseRatio: "خریداری کا تناسب",
    inventoryOperationalHealth: "انوینٹری کی عملی حالت", healthy: "صحت مند", stock: "اسٹاک", customers: "گاہک", returns: "واپسی", health: "حالت",
    lowStock: "کم اسٹاک", totalPurchases: "کل خریداری", currentMonth: "موجودہ مہینہ", totalPaid: "کل ادا شدہ", nonReturnedSales: "غیر واپس شدہ فروخت",
    totalDues: "کل واجبات", outstandingSales: "غیر واپس شدہ فروخت کے بقایا جات", registeredCustomers: "رجسٹرڈ گاہک", activeSupplierNetwork: "فعال سپلائر نیٹ ورک",
    currentStockValuation: "موجودہ اسٹاک کی مالیت", activeInventoryItems: "فعال انوینٹری اشیاء", requiresReorder: "دوبارہ آرڈر درکار", urgent: "فوری",
    invoicesToday: "آج کی رسیدیں", amount: "رقم", activeEmployees: "فعال ملازمین"
  },
  ps: {
    todaysSales: "د نن ورځې پلور", totalCustomers: "ټول پېرودونکي", suppliers: "عرضه کوونکي", products: "محصولات",
    inventoryValue: "د موجودۍ ارزښت", lowStockAlert: "د کمې ذخیرې خبرتیا", totalReturns: "ټولې بېرته ستنېدنې", totalEmployees: "ټول کارکوونکي",
    cardsView: "کارتونه", chartsView: "چارټونه", revenueTrend: "د پلور او عاید بهیر", inventoryTrend: "د ذخیرې او موجودۍ حالت",
    businessComparison: "پلور او پېرودنې", overview: "عمومي شننه", overviewSub: "ژوندۍ سوداګریزې څرګندونې او فعالیت",
    noData: "تحلیلي معلومات نشته", loadingAnalytics: "شننې بارېږي", preparingDashboard: "ستاسو سوداګریز ډشبورډ چمتو کېږي...",
    refreshDashboard: "ډشبورډ تازه کړئ", salesMovement: "د پلور پړاو په پړاو خوځښت", todaysRevenue: "د نن ورځې عاید", trending: "مخ پر ودې",
    comparisonMovement: "پرتلیز خوځښت", sales: "پلور", purchases: "پېرودنې", paid: "ورکړل شوي", dues: "پاتې", purchaseRatio: "د پېرودنې تناسب",
    inventoryOperationalHealth: "د موجودۍ عملیاتي حالت", healthy: "روغ", stock: "ذخیره", customers: "پېرودونکي", returns: "بېرته ستنېدنې", health: "حالت",
    lowStock: "کمه ذخیره", totalPurchases: "ټولې پېرودنې", currentMonth: "روانه میاشت", totalPaid: "ټولې ورکړې", nonReturnedSales: "نه ستنېدلی پلور",
    totalDues: "ټول پاتې", outstandingSales: "د نه ستنېدلي پلور پاتې", registeredCustomers: "ثبت شوي پېرودونکي", activeSupplierNetwork: "فعال عرضه کوونکي",
    currentStockValuation: "د اوسنۍ ذخیرې ارزښت", activeInventoryItems: "فعال موجودي توکي", requiresReorder: "بیا فرمایش ته اړتیا", urgent: "بیړنی",
    invoicesToday: "د نن ورځې بیلونه", amount: "اندازه", activeEmployees: "فعال کارکوونکي"
  },
  ar: {
    todaysSales: "مبيعات اليوم", totalCustomers: "إجمالي العملاء", suppliers: "الموردون", products: "المنتجات",
    inventoryValue: "قيمة المخزون", lowStockAlert: "تنبيه انخفاض المخزون", totalReturns: "إجمالي المرتجعات", totalEmployees: "إجمالي الموظفين",
    cardsView: "بطاقات", chartsView: "مخططات", revenueTrend: "اتجاه المبيعات والإيرادات", inventoryTrend: "صحة المخزون والجرد",
    businessComparison: "المبيعات مقابل المشتريات", overview: "التحليلات العامة", overviewSub: "ذكاء أعمال وأداء مباشر",
    noData: "لا تتوفر تحليلات", loadingAnalytics: "جارٍ تحميل التحليلات", preparingDashboard: "جارٍ إعداد لوحة أعمالك...",
    refreshDashboard: "تحديث لوحة التحكم", salesMovement: "حركة المبيعات خطوة بخطوة", todaysRevenue: "إيرادات اليوم", trending: "في ارتفاع",
    comparisonMovement: "حركة المقارنة", sales: "المبيعات", purchases: "المشتريات", paid: "المدفوع", dues: "المستحق", purchaseRatio: "نسبة المشتريات",
    inventoryOperationalHealth: "الصحة التشغيلية للمخزون", healthy: "سليم", stock: "المخزون", customers: "العملاء", returns: "المرتجعات", health: "الصحة",
    lowStock: "مخزون منخفض", totalPurchases: "إجمالي المشتريات", currentMonth: "الشهر الحالي", totalPaid: "إجمالي المدفوع", nonReturnedSales: "مبيعات غير مرتجعة",
    totalDues: "إجمالي المستحق", outstandingSales: "مستحقات المبيعات غير المرتجعة", registeredCustomers: "العملاء المسجلون", activeSupplierNetwork: "شبكة الموردين النشطة",
    currentStockValuation: "قيمة المخزون الحالية", activeInventoryItems: "عناصر المخزون النشطة", requiresReorder: "يحتاج إلى إعادة طلب", urgent: "عاجل",
    invoicesToday: "فواتير اليوم", amount: "المبلغ", activeEmployees: "الموظفون النشطون"
  },
  tr: {
    todaysSales: "Bugünkü Satışlar", totalCustomers: "Toplam Müşteri", suppliers: "Tedarikçiler", products: "Ürünler",
    inventoryValue: "Envanter Değeri", lowStockAlert: "Düşük Stok Uyarısı", totalReturns: "Toplam İade", totalEmployees: "Toplam Çalışan",
    cardsView: "Kartlar", chartsView: "Grafikler", revenueTrend: "Satış ve Gelir Eğilimi", inventoryTrend: "Stok ve Envanter Sağlığı",
    businessComparison: "Satışlar ve Satın Almalar", overview: "Genel Analiz", overviewSub: "Canlı iş zekâsı ve performans",
    noData: "Analiz verisi yok", loadingAnalytics: "Analizler yükleniyor", preparingDashboard: "İşletme paneliniz hazırlanıyor...",
    refreshDashboard: "Paneli yenile", salesMovement: "Adım adım satış hareketi", todaysRevenue: "Bugünkü gelir", trending: "Yükselişte",
    comparisonMovement: "Karşılaştırmalı hareket", sales: "Satışlar", purchases: "Satın Almalar", paid: "Ödenen", dues: "Alacaklar", purchaseRatio: "Satın Alma Oranı",
    inventoryOperationalHealth: "Envanter operasyon sağlığı", healthy: "Sağlıklı", stock: "Stok", customers: "Müşteriler", returns: "İadeler", health: "Sağlık",
    lowStock: "Düşük Stok", totalPurchases: "Toplam Satın Alma", currentMonth: "Geçerli ay", totalPaid: "Toplam Ödenen", nonReturnedSales: "İade edilmemiş satışlar",
    totalDues: "Toplam Alacak", outstandingSales: "İade edilmemiş satış alacakları", registeredCustomers: "Kayıtlı müşteriler", activeSupplierNetwork: "Aktif tedarikçi ağı",
    currentStockValuation: "Güncel stok değeri", activeInventoryItems: "Aktif envanter ürünleri", requiresReorder: "Yeniden sipariş gerekli", urgent: "Acil",
    invoicesToday: "bugünkü faturalar", amount: "Tutar", activeEmployees: "aktif çalışan"
  },
  fa: {
    todaysSales: "فروش امروز", totalCustomers: "کل مشتریان", suppliers: "تأمین‌کنندگان", products: "محصولات",
    inventoryValue: "ارزش موجودی", lowStockAlert: "هشدار کمبود موجودی", totalReturns: "کل مرجوعی‌ها", totalEmployees: "کل کارمندان",
    cardsView: "کارت‌ها", chartsView: "نمودارها", revenueTrend: "روند فروش و درآمد", inventoryTrend: "وضعیت انبار و موجودی",
    businessComparison: "فروش در برابر خرید", overview: "تحلیل کلی", overviewSub: "هوش تجاری و عملکرد زنده",
    noData: "داده تحلیلی موجود نیست", loadingAnalytics: "در حال بارگذاری تحلیل‌ها", preparingDashboard: "داشبورد کسب‌وکار شما آماده می‌شود...",
    refreshDashboard: "تازه‌سازی داشبورد", salesMovement: "حرکت مرحله‌به‌مرحله فروش", todaysRevenue: "درآمد امروز", trending: "رو به رشد",
    comparisonMovement: "حرکت مقایسه‌ای", sales: "فروش", purchases: "خریدها", paid: "پرداخت‌شده", dues: "مطالبات", purchaseRatio: "نسبت خرید",
    inventoryOperationalHealth: "وضعیت عملیاتی موجودی", healthy: "سالم", stock: "موجودی", customers: "مشتریان", returns: "مرجوعی‌ها", health: "وضعیت",
    lowStock: "موجودی کم", totalPurchases: "کل خریدها", currentMonth: "ماه جاری", totalPaid: "کل پرداخت‌شده", nonReturnedSales: "فروش‌های مرجوع‌نشده",
    totalDues: "کل مطالبات", outstandingSales: "مطالبات فروش‌های مرجوع‌نشده", registeredCustomers: "مشتریان ثبت‌شده", activeSupplierNetwork: "شبکه فعال تأمین‌کنندگان",
    currentStockValuation: "ارزش فعلی موجودی", activeInventoryItems: "اقلام فعال موجودی", requiresReorder: "نیازمند سفارش مجدد", urgent: "فوری",
    invoicesToday: "فاکتورهای امروز", amount: "مبلغ", activeEmployees: "کارمندان فعال"
  },
  prs: {
    todaysSales: "فروش امروز", totalCustomers: "مجموع مشتریان", suppliers: "تهیه‌کنندگان", products: "محصولات",
    inventoryValue: "ارزش موجودی", lowStockAlert: "هشدار کمبود ذخیره", totalReturns: "مجموع برگشتی‌ها", totalEmployees: "مجموع کارمندان",
    cardsView: "کارت‌ها", chartsView: "نمودارها", revenueTrend: "روند فروش و عواید", inventoryTrend: "وضعیت ذخیره و موجودی",
    businessComparison: "فروش در برابر خرید", overview: "تحلیل عمومی", overviewSub: "اطلاعات زنده تجارت و عملکرد",
    noData: "اطلاعات تحلیلی موجود نیست", loadingAnalytics: "تحلیل‌ها بارگذاری می‌شود", preparingDashboard: "داشبورد تجارتی شما آماده می‌شود...",
    refreshDashboard: "تازه‌سازی داشبورد", salesMovement: "حرکت مرحله‌به‌مرحله فروش", todaysRevenue: "عواید امروز", trending: "رو به رشد",
    comparisonMovement: "حرکت مقایسه‌ای", sales: "فروش", purchases: "خریدها", paid: "پرداخت‌شده", dues: "باقیات", purchaseRatio: "نسبت خرید",
    inventoryOperationalHealth: "وضعیت عملیاتی موجودی", healthy: "سالم", stock: "ذخیره", customers: "مشتریان", returns: "برگشتی‌ها", health: "وضعیت",
    lowStock: "ذخیره کم", totalPurchases: "مجموع خریدها", currentMonth: "ماه جاری", totalPaid: "مجموع پرداخت‌شده", nonReturnedSales: "فروش‌های برگشت‌نشده",
    totalDues: "مجموع باقیات", outstandingSales: "باقیات فروش‌های برگشت‌نشده", registeredCustomers: "مشتریان ثبت‌شده", activeSupplierNetwork: "شبکه فعال تهیه‌کنندگان",
    currentStockValuation: "ارزش فعلی ذخیره", activeInventoryItems: "اقلام فعال موجودی", requiresReorder: "نیازمند سفارش دوباره", urgent: "عاجل",
    invoicesToday: "بل‌های امروز", amount: "مبلغ", activeEmployees: "کارمندان فعال"
  }
};


/* ============================================================
   COUNT-UP ANIMATION
============================================================ */

const useCountUp = (
  rawValue,
  duration = 550
) => {
  const [displayValue, setDisplayValue] =
    useState(Number(rawValue) || 0);

  useEffect(() => {
    const endValue = Number(rawValue) || 0;
    const startValue = 0;

    let animationFrame;
    let startedAt = null;

    const animate = (time) => {
      if (!startedAt) {
        startedAt = time;
      }

      const elapsed =
        time - startedAt;

      const progress =
        Math.min(
          elapsed / duration,
          1
        );

      const eased =
        1 -
        Math.pow(
          1 - progress,
          3
        );

      setDisplayValue(
        startValue +
          (endValue - startValue) *
            eased
      );

      if (progress < 1) {
        animationFrame =
          requestAnimationFrame(
            animate
          );
      }
    };

    animationFrame =
      requestAnimationFrame(
        animate
      );

    return () =>
      cancelAnimationFrame(
        animationFrame
      );
  }, [rawValue, duration]);

  return displayValue;
};


/* ============================================================
   MAIN SALES CHART NODE

   Sharp point + vertical leader + number
============================================================ */

const SalesValueNode = ({
  cx,
  cy,
  value,
  stroke
}) => {
  if (
    cx === undefined ||
    cy === undefined
  ) {
    return null;
  }

  return (
    <g>
      {/* Node */}
      <circle
        cx={cx}
        cy={cy}
        r="3.6"
        fill={stroke}
        stroke="var(--oz-surface)"
        strokeWidth="2"
      />

      {/* Vertical leader */}
      <line
        x1={cx}
        y1={cy - 5}
        x2={cx}
        y2={cy - 19}
        stroke={stroke}
        strokeWidth="1"
        opacity="0.72"
      />

      {/* Tiny end node */}
      <circle
        cx={cx}
        cy={cy - 19}
        r="1.6"
        fill={stroke}
      />

      {/* Value */}
      <text
        x={cx}
        y={cy - 25}
        textAnchor="middle"
        className="oz-main-node-value"
      >
        {formatNodeValue(value)}
      </text>
    </g>
  );
};


/* ============================================================
   SALES VS PURCHASES NODE

   Sales labels go UP.
   Purchase labels go DOWN.
============================================================ */

const ComparisonValueNode = ({
  cx,
  cy,
  value,
  stroke,
  direction = "up"
}) => {
  if (
    cx === undefined ||
    cy === undefined
  ) {
    return null;
  }

  const isUp =
    direction === "up";

  const leaderY =
    isUp
      ? cy - 14
      : cy + 14;

  const textY =
    isUp
      ? cy - 19
      : cy + 25;

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r="3"
        fill={stroke}
        stroke="var(--oz-surface)"
        strokeWidth="1.7"
      />

      <line
        x1={cx}
        y1={
          isUp
            ? cy - 4
            : cy + 4
        }
        x2={cx}
        y2={leaderY}
        stroke={stroke}
        strokeWidth="0.9"
        opacity="0.72"
      />

      <circle
        cx={cx}
        cy={leaderY}
        r="1.3"
        fill={stroke}
      />

      <text
        x={cx}
        y={textY}
        textAnchor="middle"
        className={
          isUp
            ? "oz-compare-node-value sales"
            : "oz-compare-node-value purchase"
        }
      >
        {formatNodeValue(value)}
      </text>
    </g>
  );
};


/* ============================================================
   CLEAN SUMMARY CARD
   NO SPARKLINES / NO LINE GRAPH
============================================================ */


const SummaryStatCard = ({
  title,
  value,
  icon: Icon,
  subtitle,
  themeColor,
  isCurrency = false,
  trend,
  danger = false,
  dateLabel,
  onClick
}) => {
  const numericValue = Number(value) || 0;

  const animatedValue = useCountUp(numericValue);

  const displayValue = isCurrency
    ? formatPKR(animatedValue)
    : Math.round(animatedValue).toLocaleString("en-PK");

  return (
    <button
      type="button"
      className={`oz-summary-card ${
        danger ? "oz-summary-danger" : ""
      }`}
      onClick={onClick}
      style={{
        "--summary-accent": danger
          ? DANGER_RED
          : themeColor
      }}
    >
      {/* =====================================================
          TOP ROW
          ICON + TITLE LEFT
          DATE (+ TREND) UPPER RIGHT
      ===================================================== */}

      <div className="oz-summary-card-top">

        <div className="oz-summary-heading">
          <span className="oz-summary-icon">
            <Icon />
          </span>

          <span className="oz-summary-title">
            {title}
          </span>
        </div>

        <div className="oz-summary-card-right">
          {trend && (
            <span
              className={`oz-summary-trend ${
                danger ? "danger" : ""
              }`}
            >
              {!danger && <FaArrowUp />}

              {trend}
            </span>
          )}

          {dateLabel && (
            <span className="oz-corner-date">
              {dateLabel}
            </span>
          )}
        </div>

      </div>

      {/* =====================================================
          VALUE + SUBTITLE
      ===================================================== */}

      <div className="oz-summary-content">

        <strong className="oz-summary-value">
          {displayValue}
        </strong>

        {subtitle && (
          <span className="oz-summary-subtitle">
            {subtitle}
          </span>
        )}

      </div>
    </button>
  );
};

/* ============================================================
   REVENUE TOOLTIP
============================================================ */

const RevenueTooltip = ({
  active,
  payload,
  label
}) => {
  if (
    !active ||
    !payload?.length
  ) {
    return null;
  }

  return (
    <div className="oz-chart-tooltip">
      <span className="oz-tooltip-title">
        {label}
      </span>

      {payload.map(
        (entry) => (
          <div
            className="oz-tooltip-row"
            key={`${entry.dataKey}-${entry.value}`}
          >
            <span
              className="oz-tooltip-dot"
              style={{
                background:
                  entry.color ||
                  entry.stroke
              }}
            />

            <span className="oz-tooltip-name">
              {entry.name}
            </span>

            <strong>
              {formatPKR(
                entry.value
              )}
            </strong>
          </div>
        )
      )}
    </div>
  );
};


/* ============================================================
   MAIN COMPONENT
============================================================ */

const CosmeticsDashboardHome = ({
  onNavigate,
  setFormContent,
  onBreadcrumbUpdate
}) => {
  const auth = useAuth();
  const {
    user,
    currentShop,
    loading: authLoading
  } = auth;

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    currentLanguage,
    setCurrentLanguage
  ] = useState(
    () => {
      const requested =
        auth?.currentLanguage ||
        auth?.language ||
        user?.language ||
        user?.preferred_language ||
        localStorage.getItem(
          "ourzest_language"
        ) || "en";

      return DASHBOARD_LANGUAGES[requested]
        ? requested
        : "en";
    }
  );

  const translations = useMemo(
    () => ({
      ...DASHBOARD_TRANSLATIONS.en,
      ...(DASHBOARD_TRANSLATIONS[currentLanguage] || {})
    }),
    [currentLanguage]
  );

  const dateText = { ...DATE_LABELS.en, ...(DATE_LABELS[currentLanguage] || {}) };
  const [dateFilters, setDateFilters] = useState(() => dashboardRange("today"));
  const [rangeMode, setRangeMode] = useState("today");
  const [customStart, setCustomStart] = useState(() => dashboardRange("today").startDate);
  const [customEnd, setCustomEnd] = useState(() => dashboardRange("today").endDate);
  const [rangeError, setRangeError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [loadedRange, setLoadedRange] = useState(null);
  const requestRef = useRef(null);
  const requestVersion = useRef(0);
  const chooseRange = preset => {
    setRangeMode(preset); setRangeError("");
    if (preset === "custom") { setCustomStart(dateFilters.startDate); setCustomEnd(dateFilters.endDate); return; }
    setLoadedRange(null); setLoading(true);
    setDateFilters(dashboardRange(preset));
  };
  const applyCustomRange = event => {
    event.preventDefault();
    if (!validDashboardDates(customStart, customEnd)) { setRangeError(dateText.invalid); return; }
    setRangeError("");
    setLoadedRange(null); setLoading(true);
    setDateFilters({ preset: "custom", startDate: customStart, endDate: customEnd });
  };
  const rangeCaption = loadedRange
    ? `${loadedRange.start_date} — ${loadedRange.end_date} (${DASHBOARD_TIMEZONE})`
    : `${dateFilters.startDate} — ${dateFilters.endDate} (${DASHBOARD_TIMEZONE})`;

  const shortRangeLabel = loadedRange
    ? `${loadedRange.start_date} – ${loadedRange.end_date}`
    : `${dateFilters.startDate} – ${dateFilters.endDate}`;

  const dashboardLocale =
    DASHBOARD_LANGUAGES[currentLanguage]?.locale ||
    "en-PK";

  const [
    viewMode,
    setViewMode
  ] = useState("charts");

  /* =========================================================
     THEME COLOR STATE
  ========================================================= */

  const [
    themeColor,
    setThemeColor
  ] = useState(
    getDashboardThemeColor
  );


  /* =========================================================
     STATS
  ========================================================= */

  const [
    stats,
    setStats
  ] = useState({
    totalSuppliers: 0,
    totalCustomers: 0,
    totalProducts: 0,
    lowStockCount: 0,
    inventoryValue: 0,
    periodSales: 0,
    periodSalesCount: 0,
    totalSales: 0,
    totalPurchases: 0,
    totalPaid: 0,
    totalDues: 0
  });


  const [
    moduleStats,
    setModuleStats
  ] = useState({
    totalReturns: 0,
    totalReturnsAmount: 0,
    totalEmployees: 0,
    activeEmployees: 0
  });


  const [
    salesTrendData,
    setSalesTrendData
  ] = useState([]);


  /* =========================================================
     KEEP LANGUAGE SYNCED WITH AUTHCONTEXT + LOCALSTORAGE
  ========================================================= */

  useEffect(() => {
    const contextLanguage =
      auth?.currentLanguage ||
      auth?.language ||
      user?.language ||
      user?.preferred_language;

    if (
      contextLanguage &&
      DASHBOARD_LANGUAGES[contextLanguage]
    ) {
      setCurrentLanguage(contextLanguage);
    }
  }, [
    auth?.currentLanguage,
    auth?.language,
    user?.language,
    user?.preferred_language
  ]);

  useEffect(() => {
    const syncLanguage = (event) => {
      const requested =
        event?.detail?.language ||
        localStorage.getItem(
          "ourzest_language"
        );

      if (
        requested &&
        DASHBOARD_LANGUAGES[requested]
      ) {
        setCurrentLanguage(requested);
      }
    };

    window.addEventListener(
      "ourzest:language-change",
      syncLanguage
    );
    window.addEventListener(
      "storage",
      syncLanguage
    );

    return () => {
      window.removeEventListener(
        "ourzest:language-change",
        syncLanguage
      );
      window.removeEventListener(
        "storage",
        syncLanguage
      );
    };
  }, []);


  /* =========================================================
     KEEP THEME SYNCED
  ========================================================= */

  useEffect(() => {
    const syncTheme =
      () => {
        const nextColor =
          getDashboardThemeColor();

        setThemeColor(
          (current) =>
            current !== nextColor
              ? nextColor
              : current
        );
      };

    /*
     * Changes made by another tab/window.
     */
    window.addEventListener(
      "storage",
      syncTheme
    );

    /*
     * Works if your application's Theme button emits
     * one of these custom events.
     */
    window.addEventListener(
      "themechange",
      syncTheme
    );

    window.addEventListener(
      "ourzest-theme-change",
      syncTheme
    );

    /*
     * Watches class/style changes when the app changes
     * theme through documentElement.
     */
    const observer =
      new MutationObserver(
        syncTheme
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "class",
          "style",
          "data-theme"
        ]
      }
    );

    /*
     * Native 'storage' does NOT fire in the same tab that
     * writes localStorage, therefore this very lightweight
     * sync guarantees the dashboard follows LocalStorage.
     */
    const interval =
      window.setInterval(
        syncTheme,
        700
      );

    syncTheme();

    return () => {
      window.removeEventListener(
        "storage",
        syncTheme
      );

      window.removeEventListener(
        "themechange",
        syncTheme
      );

      window.removeEventListener(
        "ourzest-theme-change",
        syncTheme
      );

      observer.disconnect();

      window.clearInterval(
        interval
      );
    };
  }, []);


  /* =========================================================
     NAVIGATION
  ========================================================= */

  const navigateToMenu =
    useCallback(
      (
        menuName,
        submenuName = null
      ) => {
        if (onNavigate) {
          onNavigate(
            menuName,
            submenuName
          );
        }
      },
      [onNavigate]
    );


  /* =========================================================
     DASHBOARD API
  ========================================================= */

  const loadDashboardData = useCallback(async () => {
    requestRef.current?.abort();
    const version = ++requestVersion.current;
    const controller = new AbortController(); requestRef.current = controller;
    if (!user || !currentShop) { setLoading(false); return; }
    setLoading(true); setLoadError(""); setLoadedRange(null);
    try {
      const filters = dateFilters.preset === "custom" ? dateFilters : dashboardRange(dateFilters.preset);
      const result = await fetchDashboardAnalytics(currentShop.shop_id, filters, user?.token, controller.signal);
      if (controller.signal.aborted || version !== requestVersion.current) return;
      const v = result.stats || {};
      setStats({
        totalSuppliers: Number(v.total_suppliers) || 0, totalCustomers: Number(v.total_customers) || 0,
        totalProducts: Number(v.total_products) || 0, lowStockCount: Number(v.low_stock_count) || 0,
        inventoryValue: Number(v.inventory_value) || 0,
        periodSales: Number(v.total_sales) || 0, periodSalesCount: Number(v.sales_count) || 0,
        totalSales: Number(v.total_sales) || 0, totalPurchases: Number(v.total_purchases) || 0,
        totalPaid: Number(v.total_paid) || 0, totalDues: Number(v.total_dues) || 0
      });
      setModuleStats({ totalReturns: Number(v.total_returns) || 0, totalReturnsAmount: Number(v.total_returns_amount) || 0,
        totalEmployees: Number(v.total_employees) || 0, activeEmployees: Number(v.active_employees) || 0 });
      setSalesTrendData((result.trend || []).map(p => ({ date: p.date, time: p.date, sales: Number(p.sales) || 0,
        purchases: Number(p.purchases) || 0, paid: Number(p.paid) || 0, dues: Number(p.dues) || 0 })));
      setLoadedRange(result.filters || { start_date: filters.startDate, end_date: filters.endDate });
    } catch (error) {
      if (controller.signal.aborted || version !== requestVersion.current) return;
      setLoadError(error.message || "Unable to load dashboard analytics");
    } finally {
      if (!controller.signal.aborted && version === requestVersion.current) setLoading(false);
    }
  }, [user, currentShop, dateFilters]);
  useEffect(() => {
    if (!authLoading) loadDashboardData();
    return () => requestRef.current?.abort();
  }, [authLoading, loadDashboardData]);

  /* =========================================================
     SPIDER / RADAR DATA
  ========================================================= */

  const localizedSalesTrendData = useMemo(
    () => salesTrendData.map((point) => ({
      ...point,
      time: point.date
        ? new Date(`${point.date}T00:00:00`).toLocaleDateString(
            dashboardLocale,
            { month: "short", day: "numeric" }
          )
        : point.time
    })),
    [salesTrendData, dashboardLocale]
  );

  const inventoryRadarData =
    useMemo(() => {
      const products =
        Number(
          stats.totalProducts
        ) || 0;

      const lowStock =
        Number(
          stats.lowStockCount
        ) || 0;

      const suppliers =
        Number(
          stats.totalSuppliers
        ) || 0;

      const customers =
        Number(
          stats.totalCustomers
        ) || 0;

      const invoices =
        Number(
          stats.periodSalesCount
        ) || 0;

      const returns =
        Number(
          moduleStats.totalReturns
        ) || 0;


      const stockHealth =
        products > 0
          ? Math.max(
              10,
              Math.min(
                100,
                100 -
                  (lowStock /
                    products) *
                    100
              )
            )
          : 65;


      const productRange =
        Math.max(
          25,
          Math.min(
            100,
            products / 3
          )
        );


      const supplierNetwork =
        Math.max(
          20,
          Math.min(
            100,
            suppliers * 5
          )
        );


      const customerActivity =
        Math.max(
          20,
          Math.min(
            100,
            customers / 2
          )
        );


      const salesActivity =
        Math.max(
          20,
          Math.min(
            100,
            invoices * 4
          )
        );


      const returnHealth =
        Math.max(
          15,
          Math.min(
            100,
            100 -
              returns * 4
          )
        );


      return [
        {
          metric: translations.stock,
          value:
            Math.round(
              stockHealth
            )
        },

        {
          metric: translations.products,
          value:
            Math.round(
              productRange
            )
        },

        {
          metric: translations.suppliers,
          value:
            Math.round(
              supplierNetwork
            )
        },

        {
          metric: translations.customers,
          value:
            Math.round(
              customerActivity
            )
        },

        {
          metric: translations.sales,
          value:
            Math.round(
              salesActivity
            )
        },

        {
          metric: translations.returns,
          value:
            Math.round(
              returnHealth
            )
        }
      ];
    }, [
      stats,
      moduleStats,
      translations
    ]);


  /* =========================================================
     PURCHASE RATIO
  ========================================================= */

  const purchaseRatio =
    stats.periodSales > 0
      ? (
          (stats.totalPurchases /
            stats.periodSales) *
          100
        ).toFixed(0)
      : 0;


  /* =========================================================
     LOADER
  ========================================================= */

  if (
    authLoading
  ) {
    return (
      <div
        className="oz-dashboard-loader"
        lang={currentLanguage}
        dir={DASHBOARD_LANGUAGES[currentLanguage]?.dir || "ltr"}
      >
        <div className="oz-dashboard-spinner" />

        <strong>
          {translations.loadingAnalytics}
        </strong>

        <span>
          {translations.preparingDashboard}
        </span>
      </div>
    );
  }


  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div
      className="oz-analytics-dashboard"
      lang={currentLanguage}
      dir={DASHBOARD_LANGUAGES[currentLanguage]?.dir || "ltr"}
      style={{
        "--dash-accent":
          themeColor
      }}
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <section className="oz-analytics-header">
        <div className="oz-analytics-heading">
          <div className="oz-heading-icon">
            <FiActivity />
          </div>

          <div>
            <h2>
              {translations.overview ||
                "Overview Analytics"}
            </h2>

            <p>
              {translations.overviewSub ||
                "Live business intelligence & performance"}
            </p>
          </div>
        </div>


        <div className="oz-analytics-actions">
          <div className="oz-view-selector">
            <button
              type="button"
              className={
                viewMode ===
                "charts"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setViewMode(
                  "charts"
                )
              }
            >
              <FaChartLine />

              <span>
                {translations.chartsView ||
                  "Charts"}
              </span>
            </button>


            <button
              type="button"
              className={
                viewMode ===
                "cards"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setViewMode(
                  "cards"
                )
              }
            >
              <FaThLarge />

              <span>
                {translations.cardsView ||
                  "Cards"}
              </span>
            </button>
          </div>


          <button
            type="button"
            className="oz-refresh-button"
            onClick={
              loadDashboardData
            }
            title={translations.refreshDashboard}
            aria-label={translations.refreshDashboard}
          >
            <FaSyncAlt />
          </button>
        </div>
      </section>


      <section className="oz-date-panel" aria-label={dateText.range}>
        <div className="oz-date-presets" role="group" aria-label={dateText.range}>
          {["today", "yesterday", "last_week", "last_month", "custom"].map(preset => (
            <button type="button" key={preset} className={`oz-date-button ${rangeMode === preset ? "active" : ""}`}
              aria-pressed={rangeMode === preset} onClick={() => chooseRange(preset)}>{dateText[preset]}</button>
          ))}
        </div>
        {rangeMode === "custom" && <form className="oz-date-custom" onSubmit={applyCustomRange}>
          <label htmlFor="oz-dashboard-start">{dateText.from}<input id="oz-dashboard-start" type="date" required
            value={customStart} max={customEnd || undefined} onChange={e => setCustomStart(e.target.value)} /></label>
          <label htmlFor="oz-dashboard-end">{dateText.to}<input id="oz-dashboard-end" type="date" required
            value={customEnd} min={customStart || undefined} onChange={e => setCustomEnd(e.target.value)} /></label>
          <button type="submit" className="oz-date-apply">{dateText.apply}</button>
        </form>}
        {rangeError && <p className="oz-date-error" role="alert">{rangeError}</p>}
        <p className="oz-date-caption">{rangeCaption}</p>
        <p className="oz-date-note">{dateText.current}</p>
      </section>
      {loading && <p className="oz-date-feedback" role="status">{dateText.loading}</p>}
      {loadError && <p className="oz-date-error" role="alert">{loadError}</p>}

      {/* ===================================================
          CHART VIEW
      =================================================== */}

      {loadedRange && !loading && !loadError && viewMode ===
        "charts" && (
        <div className="oz-chart-dashboard">

          {/* ===============================================
              MAIN SALES TREND
          =============================================== */}

          <section className="oz-chart-panel oz-main-sales-panel">
            <div className="oz-chart-panel-header">
              <div className="oz-chart-title-wrap">
                <span className="oz-chart-title-icon themed">
                  <FiTrendingUp />
                </span>

                <div>
                  <h3>
                    {translations.revenueTrend ||
                      "Sales & Revenue Trend"}
                  </h3>

                  <p>
                    {translations.salesMovement}
                  </p>
                </div>
              </div>


              <div className="oz-panel-header-right">
                <span className="oz-corner-date">
                  {shortRangeLabel}
                </span>

                <div className="oz-sales-summary">
                  <div className="oz-sales-summary-value">
                    <strong>
                      {formatCompactPKR(
                        stats.periodSales
                      )}
                    </strong>

                    <span>
                      {dateText.periodSales}
                    </span>
                  </div>



                </div>
              </div>
            </div>


            <div className="oz-main-chart-area">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <LineChart
                  data={
                    localizedSalesTrendData
                  }
                  margin={{
                    top: 37,
                    right: 24,
                    left: -5,
                    bottom: 2
                  }}
                >
                  <CartesianGrid
                    stroke="var(--oz-chart-grid)"
                    strokeDasharray="1 3"
                    vertical
                  />


                  <XAxis
                    dataKey="time"
                    axisLine={{
                      stroke:
                        "var(--oz-axis)"
                    }}
                    tickLine={
                      false
                    }
                    tick={{
                      fill:
                        "var(--oz-muted)",
                      fontSize: 10
                    }}
                    dy={8}
                  />


                  <YAxis
                    axisLine={{
                      stroke:
                        "var(--oz-axis)"
                    }}
                    tickLine={
                      false
                    }
                    width={60}
                    tick={{
                      fill:
                        "var(--oz-muted)",
                      fontSize: 10
                    }}
                    tickFormatter={
                      formatCompactPKR
                    }
                  />


                  <RechartsTooltip
                    cursor={{
                      stroke:
                        themeColor,
                      strokeWidth:
                        1,
                      strokeDasharray:
                        "3 4",
                      opacity:
                        0.5
                    }}
                    content={
                      <RevenueTooltip />
                    }
                  />


                  <Line
                    type="linear"
                    dataKey="sales"
                    name={translations.sales}
                    stroke={
                      themeColor
                    }
                    strokeWidth={2.2}
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                    isAnimationActive
                    animationDuration={
                      700
                    }
                    dot={(props) => (
                      <SalesValueNode
                        {...props}
                        stroke={
                          themeColor
                        }
                      />
                    )}
                    activeDot={{
                      r: 5.5,
                      fill:
                        themeColor,
                      stroke:
                        "var(--oz-surface)",
                      strokeWidth:
                        2.5
                    }}
                  />

                  <Line
                    type="linear"
                    dataKey="paid"
                    name={translations.paid}
                    stroke={SUCCESS_GREEN}
                    strokeWidth={2}
                    dot={false}
                    animationDuration={700}
                  />

                  <Line
                    type="linear"
                    dataKey="dues"
                    name={translations.dues}
                    stroke={DANGER_RED}
                    strokeWidth={2}
                    dot={false}
                    animationDuration={700}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>


          {/* ===============================================
              BOTTOM ROW
          =============================================== */}

          <div className="oz-secondary-chart-grid">

            {/* =============================================
                SALES VS PURCHASES
            ============================================= */}

            <section className="oz-chart-panel oz-comparison-panel">
              <div className="oz-chart-panel-header compact">
                <div className="oz-chart-title-wrap">
                  <span className="oz-chart-title-icon yellow">
                    <FaShoppingBag />
                  </span>

                  <div>
                    <h3>
                      {translations.businessComparison ||
                        "Sales vs Purchases"}
                    </h3>

                    <p>
                      {translations.comparisonMovement}
                    </p>
                  </div>
                </div>


                <div className="oz-panel-header-right">
                  <span className="oz-corner-date">
                    {shortRangeLabel}
                  </span>

                  <div className="oz-chart-legend">
                    <span>
                      <i className="yellow" />
                      {translations.sales}
                    </span>

                    <span>
                      <i className="blue" />
                      {translations.purchases}
                    </span>

                    <span>
                      <i style={{ background: SUCCESS_GREEN }} />
                      {translations.paid}
                    </span>

                    <span>
                      <i style={{ background: DANGER_RED }} />
                      {translations.dues}
                    </span>
                  </div>
                </div>
              </div>


              <div className="oz-small-chart-area">
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <LineChart
                    data={
                      localizedSalesTrendData
                    }
                    margin={{
                      top: 32,
                      right: 16,
                      left: -12,
                      bottom: 23
                    }}
                  >
                    <CartesianGrid
                      stroke="var(--oz-chart-grid)"
                      strokeDasharray="1 4"
                      vertical
                    />


                    <XAxis
                      dataKey="time"
                      axisLine={{
                        stroke:
                          "var(--oz-axis)"
                      }}
                      tickLine={
                        false
                      }
                      interval={2}
                      tick={{
                        fill:
                          "var(--oz-muted)",
                        fontSize:
                          9
                      }}
                      dy={7}
                    />


                    <YAxis
                      axisLine={{
                        stroke:
                          "var(--oz-axis)"
                      }}
                      tickLine={
                        false
                      }
                      width={53}
                      tick={{
                        fill:
                          "var(--oz-muted)",
                        fontSize:
                          9
                      }}
                      tickFormatter={
                        formatCompactPKR
                      }
                    />


                    <RechartsTooltip
                      content={
                        <RevenueTooltip />
                      }
                    />


                    {/* SALES — YELLOW */}

                    <Line
                      type="linear"
                      dataKey="sales"
                      name={translations.sales}
                      stroke={
                        SALES_YELLOW
                      }
                      strokeWidth={
                        2.15
                      }
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      animationDuration={
                        650
                      }
                      dot={(props) => (
                        <ComparisonValueNode
                          {...props}
                          stroke={
                            SALES_YELLOW
                          }
                          direction="up"
                        />
                      )}
                      activeDot={{
                        r: 5,
                        fill:
                          SALES_YELLOW,
                        stroke:
                          "var(--oz-surface)",
                        strokeWidth:
                          2
                      }}
                    />


                    {/* PURCHASES — BLUE */}

                    <Line
                      type="linear"
                      dataKey="purchases"
                      name={translations.purchases}
                      stroke={
                        PURCHASE_BLUE
                      }
                      strokeWidth={
                        2.15
                      }
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      animationDuration={
                        800
                      }
                      dot={(props) => (
                        <ComparisonValueNode
                          {...props}
                          stroke={
                            PURCHASE_BLUE
                          }
                          direction="down"
                        />
                      )}
                    activeDot={{
                      r: 5,
                      fill:
                        PURCHASE_BLUE,
                        stroke:
                          "var(--oz-surface)",
                      strokeWidth:
                        2
                    }}
                  />

                  <Line
                    type="linear"
                    dataKey="paid"
                    name={translations.paid}
                    stroke={SUCCESS_GREEN}
                    strokeWidth={2}
                    dot={false}
                    animationDuration={700}
                  />

                  <Line
                    type="linear"
                    dataKey="dues"
                    name={translations.dues}
                    stroke={DANGER_RED}
                    strokeWidth={2}
                    dot={false}
                    animationDuration={700}
                  />
                </LineChart>
                </ResponsiveContainer>
              </div>


              <div className="oz-chart-bottom-metrics">
                <div>
                  <span>
                    {translations.sales}
                  </span>

                  <strong className="yellow-text">
                    {formatCompactPKR(
                      stats.periodSales
                    )}
                  </strong>
                </div>


                <div>
                  <span>
                    {translations.purchases}
                  </span>

                  <strong className="blue-text">
                    {formatCompactPKR(
                      stats.totalPurchases
                    )}
                  </strong>
                </div>


                <div>
                  <span>
                    {translations.purchaseRatio}
                  </span>

                  <strong>
                    {purchaseRatio}%
                  </strong>
                </div>

                <div>
                  <span>{translations.paid}</span>
                  <strong style={{ color: SUCCESS_GREEN }}>
                    {formatCompactPKR(stats.totalPaid)}
                  </strong>
                </div>

                <div>
                  <span>{translations.dues}</span>
                  <strong style={{ color: DANGER_RED }}>
                    {formatCompactPKR(stats.totalDues)}
                  </strong>
                </div>
              </div>
            </section>


            {/* =============================================
                STOCK / INVENTORY SPIDER
            ============================================= */}

            <section className="oz-chart-panel oz-radar-panel">
              <div className="oz-chart-panel-header compact">
                <div className="oz-chart-title-wrap">
                  <span className="oz-chart-title-icon themed">
                    <MdInventory />
                  </span>

                  <div>
                    <h3>
                      {translations.inventoryTrend ||
                        "Stock & Inventory Health"}
                    </h3>

                    <p>
                      {translations.inventoryOperationalHealth}
                    </p>
                  </div>
                </div>


                <div className="oz-panel-header-right">
                  <span className="oz-corner-date">
                    {shortRangeLabel}
                  </span>

                  <span className="oz-health-badge">
                    <span />
                    {translations.healthy}
                  </span>
                </div>
              </div>


              <div className="oz-radar-content">
                <div className="oz-radar-chart-area">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <RadarChart
                      data={
                        inventoryRadarData
                      }
                      outerRadius="73%"
                    >
                      <PolarGrid
                        stroke="var(--oz-chart-grid-strong)"
                      />


                      <PolarAngleAxis
                        dataKey="metric"
                        tick={{
                          fill:
                            "var(--oz-muted)",
                          fontSize:
                            9.5,
                          fontWeight:
                            600
                        }}
                      />


                      <PolarRadiusAxis
                        angle={30}
                        domain={[
                          0,
                          100
                        ]}
                        tick={
                          false
                        }
                        axisLine={
                          false
                        }
                      />


                      <Radar
                        name={translations.health}
                        dataKey="value"
                        stroke={
                          themeColor
                        }
                        strokeWidth={
                          2.1
                        }
                        fill={
                          themeColor
                        }
                        fillOpacity={
                          0.17
                        }
                        dot={{
                          r: 3.2,
                          fill:
                            themeColor,
                          stroke:
                            "var(--oz-surface)",
                          strokeWidth:
                            1.5
                        }}
                        animationDuration={
                          700
                        }
                      />


                      <RechartsTooltip
                        contentStyle={{
                          background:
                            "var(--oz-surface)",
                          border:
                            "1px solid var(--oz-border)",
                          borderRadius:
                            "9px",
                          color:
                            "var(--oz-text)",
                          fontSize:
                            "10px",
                          boxShadow:
                            "0 10px 30px rgba(0,0,0,.12)"
                        }}
                        formatter={(
                          value
                        ) => [
                          `${value}%`,
                          translations.health
                        ]}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>


                <div className="oz-inventory-quick-stats">
                  <div>
                    <span className="oz-mini-stat-icon themed">
                      <FiPackage />
                    </span>

                    <span>
                      <small>
                        {translations.products}
                      </small>

                      <strong>
                        {stats.totalProducts.toLocaleString(
                          dashboardLocale
                        )}
                      </strong>
                    </span>
                  </div>


                  <div>
                    <span className="oz-mini-stat-icon red">
                      <FiAlertCircle />
                    </span>

                    <span>
                      <small>
                        {translations.lowStock}
                      </small>

                      <strong>
                        {stats.lowStockCount.toLocaleString(
                          dashboardLocale
                        )}
                      </strong>
                    </span>
                  </div>


                  <div>
                    <span className="oz-mini-stat-icon blue">
                      <FaTruck />
                    </span>

                    <span>
                      <small>
                        {translations.suppliers}
                      </small>

                      <strong>
                        {stats.totalSuppliers.toLocaleString(
                          dashboardLocale
                        )}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}


      {/* ===================================================
          CARDS VIEW
          NO LINE GRAPHS
      =================================================== */}

      {loadedRange && !loading && !loadError && viewMode ===
        "cards" && (
        <div className="oz-summary-grid">

          <SummaryStatCard
            title={
              dateText.periodSales ||
              "Today's Sales"
            }
            value={
              stats.periodSales
            }
            icon={
              FaShoppingCart
            }
            themeColor={
              themeColor
            }
            isCurrency
            subtitle={`${stats.periodSalesCount} ${dateText.invoices}`}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "sales",
                "saleslist"
              )
            }
          />


          <SummaryStatCard
            title={translations.totalPurchases}
            value={stats.totalPurchases}
            icon={FaShoppingBag}
            themeColor={themeColor}
            isCurrency
            subtitle={rangeCaption}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "purchases",
                "purchaselist"
              )
            }
          />


          <SummaryStatCard
            title={translations.totalPaid}
            value={stats.totalPaid}
            icon={FaMoneyBillWave}
            themeColor={themeColor}
            isCurrency
            subtitle={translations.nonReturnedSales}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "sales",
                "saleslist"
              )
            }
          />


          <SummaryStatCard
            title={translations.totalDues}
            value={stats.totalDues}
            icon={FaFileInvoiceDollar}
            themeColor={themeColor}
            isCurrency
            danger={stats.totalDues > 0}
            subtitle={translations.outstandingSales}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "sales",
                "saleslist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.totalCustomers ||
              "Total Customers"
            }
            value={
              stats.totalCustomers
            }
            icon={
              FaUsers
            }
            themeColor={
              themeColor
            }
            subtitle={translations.registeredCustomers}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "customers",
                "customerlist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.suppliers ||
              "Suppliers"
            }
            value={
              stats.totalSuppliers
            }
            icon={
              FaTruck
            }
            themeColor={
              themeColor
            }
            subtitle={translations.activeSupplierNetwork}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "suppliers",
                "supplierlist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.inventoryValue ||
              "Inventory Value"
            }
            value={
              stats.inventoryValue
            }
            icon={
              MdInventory
            }
            themeColor={
              themeColor
            }
            isCurrency
            subtitle={translations.currentStockValuation}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "stock",
                "stocklist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.products ||
              "Products"
            }
            value={
              stats.totalProducts
            }
            icon={
              FaBoxes
            }
            themeColor={
              themeColor
            }
            subtitle={translations.activeInventoryItems}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "stock",
                "stocklist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.lowStockAlert ||
              "Low Stock Alert"
            }
            value={
              stats.lowStockCount
            }
            icon={
              FiAlertCircle
            }
            themeColor={
              themeColor
            }
            trend={translations.urgent}
            danger
            subtitle={translations.requiresReorder}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "stock",
                "stocklist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.totalReturns ||
              "Total Returns"
            }
            value={
              moduleStats.totalReturns
            }
            icon={
              FaArrowCircleLeft
            }
            themeColor={
              themeColor
            }
            subtitle={`${translations.amount} ${formatPKR(
              moduleStats.totalReturnsAmount
            )}`}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "returns",
                "returnlist"
              )
            }
          />


          <SummaryStatCard
            title={
              translations.totalEmployees ||
              "Total Employees"
            }
            value={
              moduleStats.totalEmployees
            }
            icon={
              FaUserCog
            }
            themeColor={
              themeColor
            }
            subtitle={`${moduleStats.activeEmployees} ${translations.activeEmployees}`}
            dateLabel={shortRangeLabel}
            onClick={() =>
              navigateToMenu(
                "employees",
                "employeeslist"
              )
            }
          />
        </div>
      )}
    </div>
  );
};


export default CosmeticsDashboardHome;